/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { homeAction } from "@redux/modules";
import { FaLock, FaChevronDown, FaInfoCircle } from "react-icons/fa";

import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";

import "./styles.scss";

const UNIT_LABELS = { ADET: "adet", KOLI: "koli", TON: "ton" };

const COLOR_PROGRESS = "#2E6DA4";
const COLOR_DONE = "#1E8449";

const STATUS_META = {
  ACTIVE: { label: "Devam ediyor", className: "ht-badge-active" },
  COMPLETED: { label: "Tamamlandı", className: "ht-badge-completed" },
  UPCOMING: { label: "Henüz başlamadı", className: "ht-badge-upcoming" },
};

/**
 * Kategorideki ürünlerin birimleri aynıysa o birimi döner, değilse boş.
 */
const resolveUnitLabel = (products) => {
  if (!products || products.length === 0) return "";
  const units = [...new Set(products.map((p) => p.unit).filter(Boolean))];
  if (units.length === 1) {
    return UNIT_LABELS[units[0]] || "";
  }
  return "";
};

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("tr-TR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch (e) {
    return "";
  }
};

/**
 * Bir hedef grubu için tüm ölçümleri (kategoriler + nokta hedefleri) üretir.
 * Her ölçüm hem bayi toplam hedefini/gerçekleşmesini (bayiTarget/bayiAchieved) hem de giriş
 * yapan BST'nin kendi payını/gerçekleşmesini (myTarget/myAchieved — myBstTarget'tan) taşır.
 * BST için dağılım yoksa myTarget null. Gerçekleşen değerler batch'ten gelir (achievedQuantity).
 */
const buildMeasures = (group) => {
  const my = group.myBstTarget || null;
  const myCategoryById = {};
  const myCategoryAchievedById = {};
  if (my) {
    (my.categories || []).forEach((c) => {
      myCategoryById[c.targetCategoryId] = c.quantity;
      myCategoryAchievedById[c.targetCategoryId] = c.achievedQuantity;
    });
  }
  const categories = group.categories || [];
  const measures = categories.map((category, ci) => ({
    key: `cat-${category.targetCategoryId ?? ci}`,
    title: category.categoryName,
    bayiTarget: category.quantity,
    bayiAchieved: category.achievedQuantity ?? 0,
    myTarget: my ? myCategoryById[category.targetCategoryId] ?? 0 : null,
    myAchieved: my ? myCategoryAchievedById[category.targetCategoryId] ?? 0 : 0,
    unitLabel: resolveUnitLabel(category.products),
  }));
  if (
    group.differentPointTarget !== null &&
    group.differentPointTarget !== undefined
  ) {
    measures.push({
      key: "different-point",
      title: "Farklı Nokta Hedefi",
      bayiTarget: group.differentPointTarget,
      bayiAchieved: group.differentPointAchieved ?? 0,
      myTarget: my ? my.differentPointTarget ?? 0 : null,
      myAchieved: my ? my.differentPointAchieved ?? 0 : 0,
      unitLabel: "nokta",
    });
  }
  if (group.newPointTarget !== null && group.newPointTarget !== undefined) {
    measures.push({
      key: "new-point",
      title: "Yeni Nokta Hedefi",
      bayiTarget: group.newPointTarget,
      bayiAchieved: group.newPointAchieved ?? 0,
      myTarget: my ? my.newPointTarget ?? 0 : null,
      myAchieved: my ? my.newPointAchieved ?? 0 : 0,
      unitLabel: "nokta",
    });
  }
  return measures;
};

/**
 * Tek bir hedef kartı (donut + başlık + gerçekleşen/hedef).
 * achieved (gerçekleşen) şu an 0; ileride hesaplanınca buraya bağlanacak.
 */
const TargetDonut = ({ title, achieved = 0, target, unitLabel }) => {
  const numericTarget = Number(target) || 0;
  const percent =
    numericTarget > 0
      ? Math.min(100, Math.round((achieved / numericTarget) * 100))
      : 0;
  const isDone = percent >= 100 && numericTarget > 0;
  const color = isDone ? COLOR_DONE : COLOR_PROGRESS;

  const radius = 52;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - percent / 100);

  return (
    <div className="ht-card">
      <div className="ht-card-title">{title}</div>
      <div className="ht-donut">
        <svg viewBox="0 0 120 120" className="ht-donut-svg">
          <circle
            cx="60"
            cy="60"
            r={radius}
            fill="none"
            stroke="#E0E6ED"
            strokeWidth="10"
          />
          <circle
            cx="60"
            cy="60"
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth="10"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            transform="rotate(-90 60 60)"
          />
        </svg>
        <div className="ht-donut-center">
          <span
            className="ht-percent"
            style={isDone ? { color: COLOR_DONE } : undefined}
          >
            {percent}%
          </span>
        </div>
      </div>
      <div className={`ht-card-sub ${isDone ? "done" : ""}`}>
        {achieved} / {numericTarget} {unitLabel}
      </div>
    </div>
  );
};

/**
 * Bayi toplam hedefi için kompakt yatay ilerleme çubuğu (Seçenek B — alt referans).
 */
const DealerBar = ({ title, achieved = 0, target, unitLabel }) => {
  const numericTarget = Number(target) || 0;
  const percent =
    numericTarget > 0
      ? Math.min(100, Math.round((achieved / numericTarget) * 100))
      : 0;
  const isDone = percent >= 100 && numericTarget > 0;
  return (
    <div className="ht-bar-item">
      <div className="ht-bar-label">{title}</div>
      <div className="ht-bar-track">
        <div
          className="ht-bar-fill"
          style={{
            width: `${percent}%`,
            background: isDone ? COLOR_DONE : COLOR_PROGRESS,
          }}
        />
      </div>
      <div className="ht-bar-text">
        {achieved} / {numericTarget} {unitLabel}
      </div>
    </div>
  );
};

/**
 * Home Çeyreklik Hedef Durumu Bölümü
 *
 * Satış temsilcisi (USER) için, bağlı olduğu bayiye atanmış hedefleri kampanya
 * dönemine göre akordiyon olarak gösterir:
 *  - Aktif dönem (Devam ediyor): açık gelir, kartları görünür.
 *  - Geçmiş dönem (Tamamlandı): kapalı; başlığa tıklayınca açılır.
 *  - Gelecek dönem (Henüz başlamadı): kilitli, açılamaz.
 * Atanmış hedef yoksa hiçbir şey render etmez.
 */
const HomeTargets = () => {
  const dispatch = useDispatch();
  const userTargetList = useSelector((store) => store.home.userTargetList);
  const isAuthenticated = useSelector((store) => store.auth.isAuthenticated);
  const [openId, setOpenId] = useState(null);
  const [openInfoId, setOpenInfoId] = useState(null);

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(homeAction.getUserTargets());
    }
  }, [dispatch, isAuthenticated]);

  // Veri geldiğinde aktif dönemi açık başlat (yoksa ilk dönemi).
  useEffect(() => {
    if (Array.isArray(userTargetList) && userTargetList.length > 0) {
      const activePeriod = userTargetList.find((p) => p.status === "ACTIVE");
      setOpenId(
        activePeriod ? activePeriod.periodId : userTargetList[0].periodId
      );
    }
  }, [userTargetList]);

  if (!userTargetList || userTargetList.length === 0) {
    return null;
  }

  return (
    <section className="home-targets">
      <Container>
        <div className="ht-section-head">
          <h2 className="ht-section-title">Çeyreklik Hedef Durumu</h2>
        </div>

        {userTargetList.map((period) => {
          const meta = STATUS_META[period.status] || STATUS_META.ACTIVE;
          const locked = period.status === "UPCOMING";
          const isOpen = !locked && openId === period.periodId;

          return (
            <div
              className={`ht-period ${
                period.status === "ACTIVE" ? "active" : ""
              } ${locked ? "locked" : ""}`}
              key={period.periodId}
            >
              <button
                type="button"
                className="ht-period-head"
                disabled={locked}
                onClick={() =>
                  !locked && setOpenId(isOpen ? null : period.periodId)
                }
              >
                <div className="ht-period-head-left">
                  <span className="ht-period-name">{period.periodName}</span>
                  <span className={`ht-badge ${meta.className}`}>
                    {meta.label}
                  </span>
                </div>
                <div className="ht-period-head-right">
                  {period.status === "ACTIVE" && period.dateEnd && (
                    <span className="ht-period-end">
                      Bitiş: {formatDate(period.dateEnd)}
                    </span>
                  )}
                  {locked ? (
                    <FaLock className="ht-lock" />
                  ) : (
                    <FaChevronDown
                      className={`ht-chevron ${isOpen ? "open" : ""}`}
                    />
                  )}
                </div>
              </button>

              {isOpen && (
                <div className="ht-period-body">
                  {(period.targetGroups || []).map((group, gi) => {
                    const measures = buildMeasures(group);
                    if (measures.length === 0) return null;
                    const hasMyTarget = Boolean(group.myBstTarget);
                    const perRow = Math.min(measures.length, 4);
                    const colSpan = 12 / perRow;
                    const hasReward =
                      Boolean(group.rewardInfo) ||
                      (group.rewardAmount !== null &&
                        group.rewardAmount !== undefined);

                    const infoKey = `${period.periodId}-${group.id ?? gi}`;
                    const isInfoOpen = openInfoId === infoKey;

                    return (
                      <div className="ht-group" key={group.id ?? gi}>
                        <div className="ht-group-label">
                          {group.targetGroupName}
                          {group.description ? (
                            <button
                              type="button"
                              className={`ht-group-info-btn ${
                                isInfoOpen ? "open" : ""
                              }`}
                              aria-label="Açıklamayı göster"
                              aria-expanded={isInfoOpen}
                              onClick={() =>
                                setOpenInfoId(isInfoOpen ? null : infoKey)
                              }
                            >
                              <FaInfoCircle className="ht-group-info" />
                            </button>
                          ) : null}
                        </div>
                        {group.description && isInfoOpen ? (
                          <div className="ht-group-desc">
                            <FaInfoCircle style={{ marginTop: 2, flexShrink: 0 }} />
                            <span>{group.description}</span>
                          </div>
                        ) : null}

                        {/* Senin Hedefin — büyük donut'lar (ana odak) */}
                        {hasMyTarget && (
                          <div className="ht-personal">
                            <div className="ht-personal-head">
                              <span>👤 Senin Hedefin</span>
                              <span className="ht-pill">Sana Düşen</span>
                            </div>
                            <Row className="g-3">
                              {measures.map((m) => (
                                <Col
                                  xs={12}
                                  sm={6}
                                  lg={colSpan}
                                  key={m.key}
                                >
                                  <TargetDonut
                                    title={m.title}
                                    achieved={m.myAchieved}
                                    target={m.myTarget}
                                    unitLabel={m.unitLabel}
                                  />
                                </Col>
                              ))}
                            </Row>
                          </div>
                        )}

                        {/* Bayi Toplam Hedefi — kompakt çubuklar (alt referans) */}
                        <div
                          className={`ht-dealer-compact ${
                            hasMyTarget ? "" : "standalone"
                          }`}
                        >
                          <div className="ht-dealer-compact-head">
                            🏪 Bayi Toplam Hedefi
                          </div>
                          <div className="ht-dealer-bars">
                            {measures.map((m) => (
                              <DealerBar
                                key={m.key}
                                title={m.title}
                                achieved={m.bayiAchieved}
                                target={m.bayiTarget}
                                unitLabel={m.unitLabel}
                              />
                            ))}
                          </div>
                        </div>

                        {/* Hediye çeki bandı (Story 3) */}
                        {hasReward && (
                          <div className="ht-reward">
                            <span className="ht-reward-icon" aria-hidden="true">
                              🎁
                            </span>
                            <div className="ht-reward-body">
                              <div className="ht-reward-title">
                                {period.status === "COMPLETED"
                                  ? "Kazandığın hediye çeki"
                                  : "Bu çeyrekte kazanabileceğin hediye çeki"}
                              </div>
                              <div className="ht-reward-sub">
                                {group.rewardInfo ||
                                  "Hedefleri tamamlarsan kazanırsın"}
                              </div>
                            </div>
                            {group.rewardAmount !== null &&
                              group.rewardAmount !== undefined && (
                                <div className="ht-reward-amount">
                                  {group.rewardAmount} TL
                                </div>
                              )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </Container>
    </section>
  );
};

export default HomeTargets;
